import { Injectable, signal } from '@angular/core';
import {
  OperationToast,
  OperationToastType,
} from '@shared/atoms/hub-operation-toasts/operation-toast.domain';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class OperationToastsService {
  private readonly toastsState = signal<OperationToast[]>([]);

  readonly toasts = this.toastsState.asReadonly();

  show(options: Partial<OperationToast>): void {
    const toast: OperationToast = {
      id: options.id ?? uuidv4(),
      textOrTpl: options.textOrTpl ?? '',
      type: options.type ?? OperationToastType.INFO,
      delay: options.delay,
      title: options.title,
      description: options.description,
      isExpanded: options.isExpanded ?? false,
      isProcessing: options.isProcessing ?? false,
      dismissible: options.dismissible ?? true,
      actions: options.actions ?? [],
      onClose: options.onClose,
      customTemplate: options.customTemplate,
      customTemplateContext: options.customTemplateContext,
    };

    this.toastsState.update((toasts) => [...toasts, toast]);
  }

  showSuccess(options: Partial<OperationToast>): void {
    this.show({ ...options, type: OperationToastType.SUCCESS });
  }

  showError(options: Partial<OperationToast>): void {
    this.show({ ...options, type: OperationToastType.ERROR });
  }

  showWarning(options: Partial<OperationToast>): void {
    this.show({ ...options, type: OperationToastType.WARNING });
  }

  showInfo(options: Partial<OperationToast>): void {
    this.show({ ...options, type: OperationToastType.INFO });
  }

  toggleExpand(toast: OperationToast): void {
    this.toastsState.update((toasts) =>
      toasts.map((t) => (t.id === toast.id ? { ...t, isExpanded: !t.isExpanded } : t)),
    );
  }

  remove(toast: OperationToast): void {
    this.toastsState.update((toasts) => toasts.filter((t) => t.id !== toast.id));
  }

  removeById(id: string): void {
    this.toastsState.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  closeById(id: string): void {
    const toast = this.toastsState().find((t) => t.id === id);
    if (toast?.onClose) {
      toast.onClose();
    }
    this.removeById(id);
  }

  update(id: string, updates: Partial<OperationToast>): void {
    this.toastsState.update((toasts) =>
      toasts.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    );
  }

  clear(): void {
    this.toastsState.set([]);
  }
}
